import React, { useEffect, useState, useRef } from "react";
import HeroSlider from "../../components/heroslider/HeroSlider";
import PostCard from "../../components/postcard/PostCard";
import Sidebar from "../../components/sidebar/Sidebar";
import Header from "../../components/Layout/Header/Header";
import SearchPopup from "../../components/SearchPopUp/SearchPopup";
import Pagination from "../../components/pagination/Pagination";
import { useNavigate } from "react-router-dom";
import { postsService, getMediaBaseUrl, Post } from "../../api/api.service";
import "../homepage/HomePage.css";

const HomePage: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [authors, setAuthors] = useState<Record<number, { name: string; avatar: string }>>({});
  const [isSearchPopupOpen, setIsSearchPopupOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  const mediaBaseUrl = getMediaBaseUrl();
  const fallbackAvatarRef = useRef(`${mediaBaseUrl}/wp-content/uploads/2025/10/profile-1.jpg`);
  const fallbackImageRef = useRef(`${mediaBaseUrl}/wp-content/uploads/2025/10/placeholder.jpg`);

  const handleSearch = (query: string) => {
    navigate(`/search?s=${encodeURIComponent(query)}`);
  };

  useEffect(() => {
    let cancelled = false;
    const fetchPosts = async () => {
      setLoading(true);
      setError(false);
      try {
        const postsData = await postsService.getPosts({
          per_page: 5,
          orderby: 'date',
          order: 'desc',
          _embed: true,
        });

        if (cancelled) return;
        setPosts(postsData);

        const authorsMap: Record<number, { name: string; avatar: string }> = {};
        postsData.forEach((p: any) => {
          authorsMap[p.author] = {
            name: p._embedded?.author?.[0]?.name || "Unknown",
            avatar:
              p._embedded?.author?.[0]?.avatar_urls?.["96"] || fallbackAvatarRef.current,
          };
        });
        setAuthors(authorsMap);
      } catch (err) {
        if (!cancelled) {
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPosts();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Header onSearchClick={() => setIsSearchPopupOpen(true)} />
      <SearchPopup
        isOpen={isSearchPopupOpen}
        onClose={() => setIsSearchPopupOpen(false)}
        onSearch={handleSearch}
      />

      <main className="home-container">
        <section className="hero-section">
          <HeroSlider />
        </section>

        <section className="main-content">
          <div className="posts-section">
            {loading && (
              <p className="status-msg">Loading posts…</p>
            )}
            {!loading && error && (
              <p className="status-msg status-error">
                Unable to load posts. Please check your connection to the API server.
              </p>
            )}
            {!loading && !error && posts.length === 0 && (
              <p className="status-msg">No posts found.</p>
            )}
            {posts.map((p) => {
              const postImage =
                p._embedded?.["wp:featuredmedia"]?.[0]?.source_url || fallbackImageRef.current;
              const excerptText = p.excerpt?.rendered.replace(/<\/?[^>]+(>|$)/g, "") || "";

              return (
                <PostCard
                  key={p.id}
                  id={p.id}
                  image={postImage}
                  title={p.title.rendered}
                  author={authors[p.author]?.name || "Unknown"}
                  authorImage={authors[p.author]?.avatar}
                  date={new Date(p.date).toDateString()}
                  excerpt={excerptText}
                />
              );
            })}

            {!loading && !error && posts.length > 0 && (
              <Pagination
                currentPage={1}
                totalPages={3}
                onPageChange={(page: number) => {
                  console.log("Switched to page:", page);
                }}
              />
            )}
          </div>

          <aside className="sidebar-section">
            <Sidebar />
          </aside>
        </section>
      </main>
    </>
  );
};

export default HomePage;
